import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid, MenuItem, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  currenciesApi, exchangeRatesApi,
  type CurrencyDto, type ExchangeRateDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { todayIso } from "@/shared/datetime";

/**
 * Currency master. Charge rows quote a rate in one of these. Exactly one currency per tenant is
 * the base (reporting) currency — promoting another demotes the incumbent, and the base can be
 * neither deactivated nor deleted.
 */
export function CurrenciesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CurrencyDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("catalog.currency.manage"));

  const query = useQuery({
    queryKey: ["currencies", search, pagination.page, pagination.pageSize],
    queryFn: () => currenciesApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Currency deleted" },
    mutationFn: (id: string) => currenciesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["currencies"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: CurrencyDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: CurrencyDto) => {
    if (confirm(`Delete currency "${row.code} — ${row.name}"? Blocked if any charge row is quoted in it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<CurrencyDto>[] = [
    { field: "code", headerName: "Code", width: 100 },
    { field: "name", headerName: "Currency", flex: 1, minWidth: 200 },
    { field: "symbol", headerName: "Symbol", width: 100, valueGetter: (_v, row) => row.symbol || "—" },
    { field: "decimalPlaces", headerName: "Decimals", width: 110, align: "right", headerAlign: "right" },
    {
      field: "isBaseCurrency", headerName: "Base", width: 120, sortable: false,
      renderCell: (p) => p.row.isBaseCurrency
        ? <StatusPill label="Reporting" tone="info" />
        : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>,
    },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage,
            disabledReason: p.row.isBaseCurrency ? "Base currency — cannot delete" : undefined,
            onClick: () => handleDelete(p.row),
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Currencies"
        subtitle="Currencies a service charge can be quoted in. One is the tenant's base reporting currency."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New currency</Button>
        ) : undefined}
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <ExchangeRatesPanel />

      <CurrencyDialog open={formOpen} onClose={() => setFormOpen(false)} row={editing} />
    </Box>
  );
}

function CurrencyDialog({ open, onClose, row }: { open: boolean; onClose: () => void; row: CurrencyDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [isBaseCurrency, setIsBaseCurrency] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? "");
    setName(row?.name ?? "");
    setSymbol(row?.symbol ?? "");
    setDecimalPlaces(row?.decimalPlaces ?? 2);
    setIsBaseCurrency(row?.isBaseCurrency ?? false);
    setIsActive(row?.isActive ?? true);
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Currency updated" : "Currency created" },
    mutationFn: async () => {
      const payload = {
        code: code.trim().toUpperCase(), name: name.trim(),
        symbol: symbol.trim() || null,
        decimalPlaces, isBaseCurrency, isActive,
      };
      if (isEdit && row) { await currenciesApi.update(row.id, payload); return row.id; }
      const r = await currenciesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["currencies"] });
      onClose();
    },
  });

  // The API rejects demoting or deactivating the base currency; keep the switches consistent here
  // so the user sees why rather than getting a 409 on save.
  const wasBase = row?.isBaseCurrency ?? false;
  const err =
    !/^[A-Za-z]{3}$/.test(code.trim()) ? "Code must be the 3-letter ISO-4217 code (INR, USD…)"
    : !name.trim() ? "Name required"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit currency" : "New currency"}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Symbol" size="small" fullWidth
                value={symbol} onChange={(e) => setSymbol(e.target.value)} inputProps={{ maxLength: 8 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Decimal places" size="small" fullWidth type="number"
                value={decimalPlaces}
                onChange={(e) => setDecimalPlaces(e.target.value === "" ? 0 : Number(e.target.value))}
                inputProps={{ min: 0, max: 4 }}
                helperText="0–4; rounds the billing screen" />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FormControlLabel
                label="Base (reporting) currency"
                control={
                  <Switch checked={isBaseCurrency} disabled={wasBase}
                    onChange={(_, v) => { setIsBaseCurrency(v); if (v) setIsActive(true); }} />
                } />
              <FormControlLabel label="Active"
                control={
                  <Switch checked={isActive} disabled={isBaseCurrency}
                    onChange={(_, v) => setIsActive(v)} />
                } />
            </Grid>
          </Grid>
          {wasBase && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This is the base currency. Promote another currency to move the flag — a tenant must
              always have exactly one, and it cannot be deactivated.
            </Alert>
          )}
          {!wasBase && isBaseCurrency && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Saving will make this the base currency and demote the current one.
            </Alert>
          )}
          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Exchange rates (W25) — what one unit of each foreign currency is worth in the tenant's base.
 *
 * Sits under the currency master because it answers the question the master raises: a charge
 * quoted in USD could always be stored and billed, it just could not be reported alongside a
 * rupee figure, because nothing knew what a dollar was worth.
 *
 * Rates are effective-dated and never edited. A correction is a new row from a new date, so a
 * March report re-run in June still converts the way it did then.
 */
function ExchangeRatesPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const canManage = useAuth(s => s.hasPermission("catalog.currency.manage"));

  const query = useQuery({
    queryKey: ["exchange-rates"],
    queryFn: () => exchangeRatesApi.list({ pageSize: 200 }),
  });

  const currencies = useQuery({
    queryKey: ["currencies", "for-rates"],
    queryFn: () => currenciesApi.list({ isActive: true, pageSize: 200 }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Rate removed" },
    mutationFn: (id: string) => exchangeRatesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exchange-rates"] }),
  });

  const baseCode = currencies.data?.items.find(c => c.isBaseCurrency)?.code;
  const foreign = (currencies.data?.items ?? []).filter(c => !c.isBaseCurrency);

  // Currencies in the master with nothing on file. Worth naming: a charge quoted in one of these
  // is a row that will report as "no rate on file" rather than convert.
  const priced = new Set((query.data?.items ?? []).map(r => r.currencyId));
  const unpriced = foreign.filter(c => !priced.has(c.id));

  const columns: GridColDef<ExchangeRateDto>[] = [
    {
      field: "currencyCode", headerName: "Currency", width: 220,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{p.row.currencyCode}</Box>
          <Box component="span" sx={{ color: "text.secondary" }}>{p.row.currencyName}</Box>
        </Box>
      ),
    },
    {
      // Read as a sentence, because the direction is the entire risk: "1 USD = 83.25 INR".
      field: "rateToBase", headerName: "Worth", flex: 1, minWidth: 260,
      renderCell: (p) => (
        <Box component="span">
          1 {p.row.currencyCode} = <b>{p.row.rateToBase.toLocaleString(undefined, {
            minimumFractionDigits: 2, maximumFractionDigits: 6,
          })}</b> {p.row.baseCurrencyCode ?? ""}
        </Box>
      ),
    },
    { field: "effectiveFrom", headerName: "From", width: 130 },
    {
      field: "isCurrent", headerName: "", width: 110, sortable: false,
      renderCell: (p) => p.row.isCurrent
        ? <StatusPill label="In force" tone="success" />
        : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>,
    },
    {
      field: "source", headerName: "Source", flex: 1, minWidth: 180,
      valueGetter: (_v, row) => row.source || "—",
    },
    {
      field: "actions", headerName: "", width: 64, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[{
          label: "Remove", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage,
          disabledReason: p.row.isCurrent ? undefined : "A later rate supersedes this one — the history stays",
          onClick: () => {
            if (confirm(`Remove the ${p.row.currencyCode} rate from ${p.row.effectiveFrom}?`)) {
              deleteMutation.mutate(p.row.id);
            }
          },
        }]} />
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Exchange rates</Typography>
          <Typography variant="body2" color="text.secondary">
            What one unit of each currency is worth in {baseCode ?? "the base currency"}. Used to
            report a rate card quoted in a contract currency. Never edited — a correction is a new
            rate from a new date, so a report re-run later still converts the way it did.
          </Typography>
        </Box>
        {canManage && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)}
            disabled={!baseCode || foreign.length === 0}>
            New rate
          </Button>
        )}
      </Box>

      {!baseCode && !currencies.isLoading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No base currency is set, so there is nothing to convert into. Mark one currency above as
          the base first.
        </Alert>
      )}
      {baseCode && unpriced.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No rate on file for {unpriced.map(c => c.code).join(", ")}. Any charge quoted in{" "}
          {unpriced.length === 1 ? "it" : "those"} will be reported as unconverted and left out of
          a total, rather than added in at face value.
        </Alert>
      )}
      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
      {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}

      <Box sx={{ height: 380 }}>
        <DataGrid
          rows={query.data?.items ?? []}
          columns={columns}
          loading={query.isLoading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Box>

      <ExchangeRateDialog
        open={open} onClose={() => setOpen(false)}
        currencies={foreign} baseCode={baseCode}
      />
    </Paper>
  );
}

function ExchangeRateDialog({ open, onClose, currencies, baseCode }: {
  open: boolean; onClose: () => void; currencies: CurrencyDto[]; baseCode?: string;
}) {
  const qc = useQueryClient();
  const [currencyId, setCurrencyId] = useState("");
  const [rate, setRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);
  const [source, setSource] = useState("");

  useEffect(() => {
    if (!open) return;
    setCurrencyId(currencies[0]?.id ?? "");
    setRate("");
    setEffectiveFrom(todayIso());
    setSource("");
  }, [open, currencies]);

  const mutation = useMutation({
    meta: { successMessage: "Rate recorded" },
    mutationFn: () => exchangeRatesApi.create({
      currencyId, rateToBase: Number(rate), effectiveFrom,
      source: source.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exchange-rates"] });
      onClose();
    },
  });

  const picked = currencies.find(c => c.id === currencyId);
  const numeric = Number(rate);
  const err =
    !currencyId ? "Pick a currency"
    : !rate.trim() || !Number.isFinite(numeric) ? "Enter the rate"
    : numeric <= 0 ? "A rate of zero would value every amount in this currency at nothing"
    : numeric > 1000000 ? "Above a million to one — check the direction, it is probably inverted"
    : !effectiveFrom ? "Pick the date it starts applying"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>New exchange rate</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField select label="Currency *" size="small" fullWidth
                value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}>
                {currencies.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField label={`Worth in ${baseCode ?? "base currency"} *`} size="small" fullWidth
                type="number" value={rate} onChange={(e) => setRate(e.target.value)}
                inputProps={{ step: "0.000001", min: 0 }}
                helperText={`Units of ${baseCode ?? "base"} per ONE ${picked?.code ?? "unit"} — 83, not 0.012`} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Effective from *" size="small" fullWidth type="date"
                value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField label="Source" size="small" fullWidth
                value={source} onChange={(e) => setSource(e.target.value)}
                inputProps={{ maxLength: 200 }}
                placeholder="RBI reference, contract fixed, bank quote…"
                helperText="An auditor asking why this number is asking a question no dropdown answers" />
            </Grid>
          </Grid>

          {/* The direction stated back as a sentence. Reading it aloud is what catches the
              inverted rate, which is otherwise a plausible-looking small number. */}
          {picked && baseCode && Number.isFinite(numeric) && numeric > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <b>1 {picked.code} = {numeric.toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 6,
              })} {baseCode}</b>
              {" — so a 100 "}{picked.code}{" charge reports as "}
              {(numeric * 100).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })} {baseCode}. If that reads wrong, the rate is inverted.
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Rates are never edited. This adds a row that takes effect on its date and stays in
            force until a later one starts — which is what lets an old report be re-run and still
            convert the way it did at the time.
          </Alert>
          {err && <Alert severity="warning" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            Record rate
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
